import type { GifMediaType, GiphyFetchPage, GiphyItem } from "@/lib/giphy";

/**
 * Demo/mock data source for the Giphy picker — used ONLY in the widget's demo
 * (`isDummy`) mode so the picker is reviewable on /example before the BE
 * proxy is deployed. Previews are self-contained SVG data-URIs (no network, no
 * possibly-rotting Giphy URLs, clearly fake), with varied heights so the
 * masonry grid, tabs, search and infinite scroll all read realistically.
 */

const PALETTE = ["#7c5cff", "#ff6b6b", "#12b886", "#f59f00", "#4dabf7", "#e64980"];
const HEIGHTS = [120, 160, 200, 140, 180, 100];
const PAGES = 3; // pretend there are 3 pages, then hasNext=false

function svgDataUri(label: string, w: number, h: number, color: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><rect width="100%" height="100%" fill="${color}"/><text x="50%" y="50%" fill="#fff" font-family="sans-serif" font-size="14" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function mockItem(type: GifMediaType, query: string, i: number): GiphyItem {
  const w = 220;
  const h = HEIGHTS[i % HEIGHTS.length];
  const color = PALETTE[i % PALETTE.length];
  const label = query
    ? `${query} ${i + 1}`
    : type === "STICKER"
      ? `sticker ${i + 1}`
      : `gif ${i + 1}`;
  const uri = svgDataUri(label, w, h, color);
  return {
    id: `mock-${type}-${query || "trending"}-${i}`,
    title: label,
    previewUrl: uri,
    previewWidth: w,
    previewHeight: h,
    sourceUrl: uri,
    mimetype: "image/svg+xml",
  };
}

const PAGE_SIZE = 15;

/** A `GiphyFetchPage` backed by generated placeholders (demo mode only). */
export const mockGiphyFetchPage: GiphyFetchPage = ({ type, query, offset }) => {
  const page = Math.floor(offset / PAGE_SIZE);
  const items = Array.from({ length: PAGE_SIZE }, (_, k) => mockItem(type, query, offset + k));
  const hasNext = page < PAGES - 1;
  // Simulate a small network delay so the loading states are visible in review.
  return new Promise((resolve) =>
    setTimeout(() => resolve({ items, nextOffset: offset + PAGE_SIZE, hasNext }), 250),
  );
};
